import type {
  AgentCreateInput,
  AgentDetail,
  AgentReasoningTier,
  AgentUpdateInput,
  AgentVerbosity,
} from "../contracts/agents.js";
import {
  type AgentDetailTab,
  normalizeAgentDetailTab,
  normalizePermissionsGroupSlug,
} from "./agent-detail.js";
import type { SpecialistTemplate } from "./api-client.js";

interface AgentFormFields {
  displayName: string;
  jobTitle: string;
  description: string;
  /** Empty string = Unassigned. Only app admins may change it. */
  teamId: string;
  toneMode: string;
  corePrompt: string;
  handle: string;
  verbosity: AgentVerbosity;
  /** `null` is the inherit choice — the agent runs on the default `standard` tier. */
  reasoningTier: AgentReasoningTier | null;
}

type AgentFormMode = "create" | "edit";

export type AgentFormValues<Mode extends AgentFormMode = AgentFormMode> = Mode extends "edit"
  ? AgentFormFields & { mode: Mode; enabled: boolean }
  : AgentFormFields & { mode: Mode };

export type AgentFormErrors = Partial<Record<keyof AgentFormFields, string>>;

export interface TemplateBinding {
  templateId: string;
  pinnedVersion: number;
}

export function resolveTemplateBinding(agent: {
  templateId?: string | null;
  templateVersion?: number | null;
  presetId?: string | null;
}): TemplateBinding | null {
  if (agent.templateId) {
    return { templateId: agent.templateId, pinnedVersion: agent.templateVersion ?? 1 };
  }
  if (agent.presetId) {
    return { templateId: agent.presetId, pinnedVersion: 1 };
  }
  return null;
}

export type AgentsRoute =
  | { page: "list" }
  | {
      /**
       * Two-step create wizard. Each step is URL-addressable so browser Back
       * maps to step navigation: `/specialists/new` is the template picker,
       * `/specialists/new/configure` the configure step (selected template
       * rides in the `?template=` query parameter; absent = Custom).
       */
      page: "new";
      step: "choose" | "configure";
    }
  | { page: "edit"; agentId: string }
  | {
      page: "detail";
      agentId: string;
      tab: AgentDetailTab;
      /** Raw `/access` route retained only for a forbidden administrator's force-join recovery. */
      recoveryAccess?: boolean;
      /**
       * Selected permission group for the Permissions-tab master-detail view.
       * Only set when `tab === "permissions"` and a group sub-route segment is
       * present; absent means the group-list landing.
       */
      permissionsGroup?: string;
      browserLive?: boolean;
    }
  | {
      /**
       * Conversations is a sub-route off the agent detail page rather than a
       * top-level tab: the per-agent conversation index at
       * `/specialists/:id/conversations`. A row opens in Workspace, so there is
       * no deep-link-to-transcript variant here.
       */
      page: "conversations";
      agentId: string;
    };

export const AGENT_TONE_OPTIONS = [
  { value: "friendly", label: "Friendly" },
  { value: "direct", label: "Direct" },
  { value: "careful", label: "Careful" },
  { value: "technical", label: "Technical" },
] as const;

export const AGENT_VERBOSITY_OPTIONS = [
  { value: "concise" as const, label: "Concise", description: "Short, direct answers." },
  { value: "balanced" as const, label: "Balanced", description: "Moderate detail." },
  {
    value: "verbose" as const,
    label: "Verbose",
    description: "Thorough answers with reasoning.",
  },
] as const;

/**
 * Per-agent reasoning-tier choices. `null` is the inherit option: the agent runs
 * on the instance default tier. Depth is not a separate control — it rides the
 * tier, so a stronger tier also thinks harder. Copy stays vendor-neutral (no
 * model names), since tier contents are Gateway config the Hub never surfaces.
 */
export const AGENT_REASONING_TIER_OPTIONS = [
  { value: null, label: "Default", description: "Use the workspace default." },
  { value: "light" as const, label: "Light", description: "Cheaper and faster." },
  { value: "standard" as const, label: "Standard", description: "Balanced cost and quality." },
  {
    value: "advanced" as const,
    label: "Advanced",
    description: "Strongest, with the most thinking.",
  },
] as const;

const CORE_PROMPT_MAX_LENGTH = 10_000;
const RESERVED_HANDLES = new Set(["everyone", "here", "channel", "admin"]);
const HANDLE_PATTERN = /^[a-z][a-z0-9-]*$/;

export function emptyAgentFormValues(): AgentFormValues<"edit"> {
  return {
    mode: "edit",
    displayName: "",
    jobTitle: "",
    description: "",
    teamId: "",
    toneMode: "friendly",
    corePrompt: "",
    enabled: true,
    handle: "",
    verbosity: "balanced",
    reasoningTier: null,
  };
}

/**
 * Strip accents to their ASCII base, then collapse every run of
 * non-alphanumerics to one hyphen. Length capping stays with the caller.
 */
function slugFold(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function suggestHandle(displayName: string): string {
  return slugFold(displayName).slice(0, 32);
}

export function isValidHandle(handle: string): boolean {
  if (handle.length < 2 || handle.length > 32) return false;
  if (!HANDLE_PATTERN.test(handle)) return false;
  if (RESERVED_HANDLES.has(handle)) return false;
  return true;
}

export function validateAgentForm(values: AgentFormValues): {
  valid: boolean;
  errors: AgentFormErrors;
} {
  const errors: AgentFormErrors = {};
  if (!values.displayName.trim()) errors.displayName = "Specialist name is required";
  if (!values.jobTitle.trim()) errors.jobTitle = "Job title is required";
  if (!values.description.trim()) errors.description = "Description is required";
  if (!values.toneMode.trim()) errors.toneMode = "Tone is required";
  if (values.corePrompt.length > CORE_PROMPT_MAX_LENGTH) {
    errors.corePrompt = "Core prompt must be 10,000 characters or fewer";
  }
  const handle = values.handle.trim();
  if (handle.length > 0 && !isValidHandle(handle)) {
    errors.handle =
      "Handle must be 2-32 lowercase letters, digits, or hyphens, starting with a letter, and not a reserved word.";
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

function formValuesToCreatePayload(values: AgentFormValues): AgentCreateInput {
  const corePrompt = values.corePrompt.trim();
  const handle = values.handle.trim();
  return {
    displayName: values.displayName.trim(),
    jobTitle: values.jobTitle.trim(),
    description: values.description.trim(),
    ...(values.teamId ? { teamId: values.teamId } : {}),
    toneMode: values.toneMode.trim(),
    ...(corePrompt ? { corePrompt } : {}),
    ...(handle ? { handle } : {}),
    verbosity: values.verbosity,
    // Always sent, `null` included: on update an absent key leaves the stored
    // tier unchanged, so moving an agent back to inherit must send `null`.
    reasoningTier: values.reasoningTier,
  };
}

export function agentFormToCreatePayload(values: AgentFormValues<"create">): AgentCreateInput {
  return formValuesToCreatePayload(values);
}

/**
 * `canAssignTeam` mirrors the Hub route's field-level admin gate: any request
 * carrying `teamId` is rejected for a non-admin, so a Manager's save must not
 * carry the key at all — not even the `null` that would otherwise mean
 * "unassign". The team control is hidden from the same callers.
 */
export function agentFormToUpdatePayload(
  values: AgentFormValues<"edit">,
  options: { canAssignTeam: boolean },
): Omit<AgentUpdateInput, "agentId"> {
  const handle = values.handle.trim();
  const { teamId: _createTeamId, ...createFields } = formValuesToCreatePayload(values);
  return {
    ...createFields,
    corePrompt: values.corePrompt.trim(),
    enabled: values.enabled,
    // `null` is the only value that unassigns; an absent key would leave the
    // agent in whichever team it is already in.
    ...(options.canAssignTeam ? { teamId: values.teamId || null } : {}),
    ...(handle ? { handle } : {}),
    verbosity: values.verbosity,
  };
}

export function agentDetailToFormValues(
  agent: AgentDetail,
  template?: Pick<SpecialistTemplate, "defaultVerbosity">,
): AgentFormValues<"edit"> {
  // Null verbosity inherits a projected template default when available;
  // Custom and removed-template agents use the product-level fallback.
  return {
    mode: "edit",
    displayName: agent.displayName,
    jobTitle: agent.jobTitle ?? "",
    description: agent.description ?? "",
    teamId: agent.teamId ?? "",
    toneMode: agent.toneMode ?? "friendly",
    corePrompt: agent.corePrompt ?? "",
    enabled: agent.enabled,
    handle: agent.handle ?? "",
    verbosity: agent.verbosity ?? template?.defaultVerbosity ?? "balanced",
    // Absent means the agent has no override and inherits the instance default;
    // the form models that as `null` (the Default choice).
    reasoningTier: agent.reasoningTier ?? null,
  };
}

export function deriveAgentsRoute(pathname: string): AgentsRoute {
  const segments = pathname
    .replace(/^\//, "")
    .split("/")
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));

  if (segments.length === 0) return { page: "list" };
  // Only the Specialists IA owns this router; a first segment that isn't
  // `specialists` is not ours, so fall back to the list landing.
  if (segments[0] !== "specialists") return { page: "list" };
  if (segments.length === 1) return { page: "list" };
  if (segments[1] === "new") {
    return { page: "new", step: segments[2] === "configure" ? "configure" : "choose" };
  }
  if (segments.length === 3 && segments[2] === "edit" && segments[1]) {
    return { page: "edit", agentId: segments[1] };
  }
  if (segments[1]) {
    // Conversations is a dedicated sub-route, not an agent-detail tab, so it is
    // matched before the tab fallback (an unrecognized third segment otherwise
    // resolves to the Overview tab). Any deeper `/chat` or
    // `/conversations/:sessionId` path is redirected to Workspace before it
    // reaches this router.
    if (segments[2] === "conversations") {
      return { page: "conversations", agentId: segments[1] };
    }
    const recoveryAccess = segments[2] === "access";
    const tab = normalizeAgentDetailTab(segments[2]);
    const permissionsGroup =
      tab === "permissions" ? normalizePermissionsGroupSlug(segments[3]) : undefined;
    return {
      page: "detail",
      agentId: segments[1],
      tab,
      ...(recoveryAccess ? { recoveryAccess: true } : {}),
      ...(permissionsGroup ? { permissionsGroup } : {}),
      ...(tab === "browser" && segments[3] === "live" ? { browserLive: true } : {}),
    };
  }
  return { page: "list" };
}
